import React from 'react';
import { AppState, defaultAppState, ModifyStateFunction } from './appState/types';
import { getViewActions } from './appState/viewActions';
import { getOrderEditActions } from './appState/editOrder';
import { getPageOrderInRoutingItem, pageReferenceIsEqual } from '../utils/orderDocumentationHelpers';
import { orderService } from '../utils/orderService';
import { Signature } from '@gkn-ili/views/dist/Signature';
import { exportToZip, import3DXML, importFromZip } from '../utils/zip';
import {
  isPageReferenceActivity,
  isPageReferenceActivityGroup,
  InspectionSignatureReference,
  OrderView,
  PageReference,
} from '@gkn-ili/views/dist/OrderDocumentation';

export function getPageContent(orderView: OrderView, ref: PageReference) {
  const routingItem = orderView.routing[ref.routingItemKey];
  if (isPageReferenceActivity(ref)) {
    return routingItem.steps[ref.stepKey].activityGroups[ref.activityGroupKey].activities[ref.activityKey];
  }
  if (isPageReferenceActivityGroup(ref)) {
    return routingItem.steps[ref.stepKey].activityGroups[ref.activityGroupKey];
  }
  return routingItem.steps[ref.stepKey];
}

const context = React.createContext<[AppState, ModifyStateFunction]>([defaultAppState, () => {}]);

function postStateCheckActivePage(newState: AppState) {
  // clean active page state, remove undefined components
  if (isPageReferenceActivity(newState.activePage) && newState.activePage.activityKey === undefined) {
    newState.activePage = {
      routingItemKey: newState.activePage.routingItemKey,
      stepKey: newState.activePage.stepKey,
      activityGroupKey: newState.activePage.activityGroupKey,
    };
  }
  if (isPageReferenceActivityGroup(newState.activePage) && newState.activePage.activityGroupKey === undefined) {
    newState.activePage = { routingItemKey: newState.activePage.routingItemKey, stepKey: newState.activePage.stepKey };
  }

  let routingItem = newState.orderView.routing[newState.activePage.routingItemKey];
  if (!routingItem) {
    newState.activePage = {
      routingItemKey: Object.keys(newState.orderView.routing)[0],
      stepKey: newState.activePage.stepKey,
    };
    routingItem = newState.orderView.routing[newState.activePage.routingItemKey];
  }

  const step = routingItem.steps[newState.activePage.stepKey];
  if (!step) {
    newState.activePage = {
      routingItemKey: newState.activePage.routingItemKey,
      stepKey: Object.keys(routingItem.steps)[0],
    };
    return;
  }

  // We are done with the checks if this is not a activity group or activity
  if (!isPageReferenceActivityGroup(newState.activePage) && !isPageReferenceActivity(newState.activePage)) {
    return;
  }
  // if the step does exists we check if the activity group still exists
  const activityGroup = step.activityGroups[newState.activePage.activityGroupKey];
  if (!activityGroup) {
    // The activity group does not exists, fallback to the complete step
    newState.activePage = {
      routingItemKey: newState.activePage.routingItemKey,
      stepKey: newState.activePage.stepKey,
    };
    return;
  }

  // Check if the page does still exist
  if (!isPageReferenceActivity(newState.activePage)) {
    return;
  }
  const page = activityGroup.activities[newState.activePage.activityKey];
  if (!page) {
    // The page does not exists, fallback to the complete substep
    newState.activePage = {
      routingItemKey: newState.activePage.routingItemKey,
      stepKey: newState.activePage.stepKey,
      activityGroupKey: newState.activePage.activityGroupKey,
    };
    return;
  }
}

export default function AppContextProvider(props: React.PropsWithChildren) {
  const [state, setFinalState] = React.useState<AppState>(() => {
    return defaultAppState;
  });

  const setState = React.useCallback(
    (reducer: (current: AppState) => AppState) => {
      setFinalState((current) => {
        const newState = reducer(current);

        // execute post modification hooks
        postStateCheckActivePage(newState);

        return newState;
      });
    },
    [setFinalState],
  );
  const orderServiceCallback = React.useCallback(
    (msg: { type: 'resourceUpdate'; data: OrderView }) => {
      setState((current) => ({ ...current, orderView: msg.data }));
    },
    [setState],
  );

  // Register callback for updates from the order-service
  React.useEffect(() => {
    orderService.on('onReceive', orderServiceCallback);
    const callbackRegistered = orderServiceCallback;
    return () => {
      orderService.off('onReceive', callbackRegistered);
    };
  }, [orderServiceCallback]);

  return <context.Provider value={[state, setState]}>{props.children}</context.Provider>;
}

export function useAppState() {
  return React.useContext(context)[0];
}

export function useAppActions() {
  const setState = React.useContext(context)[1];

  return {
    updateActivePage: (key: AppState['activePage']) => {
      setState((current) => ({ ...current, activePage: key }));
    },
    gotoNextInspectionStep: () => {
      setState((current) => {
        const routingKey = current.activePage.routingItemKey;

        const pageReferences = getPageOrderInRoutingItem(current.orderView.routing[routingKey], routingKey);

        const currentPage = current.activePage;
        const currentIdx = pageReferences.findIndex((v) => pageReferenceIsEqual(v, currentPage));
        // There is no next page if this is the last item
        if (currentIdx === pageReferences.length - 1) {
          return current;
        }

        const nextPages = pageReferences.slice(currentIdx + 1);
        const nextPage = nextPages.find(
          (v) =>
            v.stepKey !== currentPage.stepKey ||
            ('subStepKey' in v && !('subStepKey' in currentPage)) ||
            (!('subStepKey' in v) && 'subStepKey' in currentPage) ||
            ('subStepKey' in v && 'subStepKey' in currentPage && v.subStepKey !== currentPage.subStepKey),
        );

        if (nextPage) {
          return { ...current, activePage: nextPage };
        }
        // If nothing was found there is no next page
        return current;
      });
    },
    importOrder: async (file: File) => {
      if (!file.name.endsWith('.zip')) {
        return;
      }
      const result = await importFromZip(file);
      setState((current) => ({
        ...current,
        model: result.model,
        rawModel: result.rawModel,
      }));
      await orderService.loadFromFile(result.saveFile);
    },
    exportOrder: async () => {
      const result = await orderService.createSaveFile();
      setState((current) => {
        exportToZip(current.orderView.name, result, current.rawModel);
        return current;
      });
    },
    replace3DXMLModel: async (file: File) => {
      if (!file.name.endsWith('.3dxml')) {
        return;
      }
      const result = await import3DXML(file);
      setState((current) => ({
        ...current,
        model: result.model,
        rawModel: result.rawModel,
      }));
      await orderService.replaceModel(result.modelFile);
    },
    view: getViewActions(setState),
    editOrder: getOrderEditActions(setState),
    executeOrder: {
      signInspectionRecords: async (reference: InspectionSignatureReference, data: Omit<Signature, 'publicHash'>) => {
        await orderService.recordSign(reference, data);
      },
    },
  };
}
